import { useEffect, useState, useContext } from "react";
import { fetchProducts, getProductsByCategory } from "../api/apiCalls";
import {
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  Pagination,
} from "@mui/material";
import Banner from "../components/Banner";
import HomeCard from "../components/HomeCard";
import { UserContext } from "../context/UserContext";

const Home = () => {
  // Access user state from context
  const { userState } = useContext(UserContext);

  // State to store products fetched from API
  const [storeProducts, setStoreProducts] = useState([]);
  // State to track selected product category
  const [selectedCategory, setSelectedCategory] = useState("all");
  // State to handle pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;

  // Mapping between category labels and API category keys
  const categoryMapping = {
    All: "all",
    "Sweaters & Shirts": "sweaters-shirts",
    Jackets: "jackets",
    Bottoms: "pants",
    Shoes: "shoes",
    Sunglasses: "sunglasses",
    Watches: "watches",
  };

  // Fetch products when category changes
  useEffect(() => {
    const getData = async () => {
      if (selectedCategory === "all") {
        const productsData = await fetchProducts();
        setStoreProducts(productsData);
      } else {
        const productsData = await getProductsByCategory(selectedCategory);
        setStoreProducts(productsData);
      }
      setPage(1); // Reset to first page when category changes
    };

    getData();
  }, [selectedCategory]);

  // Handle category selection change
  const handleChangeCategory = (categoryLabel) => {
    const categoryKey = categoryMapping[categoryLabel];
    setSelectedCategory(categoryKey);
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Determine which products to display on the current page
  const startIndex = (page - 1) * itemsPerPage;
  const selectedProducts = storeProducts.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <Container sx={{ marginTop: { xs: "55px", sm: "65px" } }}>
      {/* Display banner component */}
      <Banner />

      {/* Greeting message based on user authentication status */}
      {userState.username ? (
        <Typography
          className="salutation"
          sx={{
            fontSize: {
              xs: "13px",
              md: "14px",
              lg: "18px",
            },
            height: { xs: "25px", md: "40px" },
          }}
        >
          Hello, {userState.fname}! Start building your cart and enjoy smooth
          shopping!
        </Typography>
      ) : (
        <Typography
          className="salutation"
          sx={{
            fontSize: {
              xs: "13px",
              md: "16px",
              lg: "18px",
            },
            height: { xs: "25px", md: "40px" },
          }}
        >
          Create an account or log in to start adding your favorite products to
          your cart!
        </Typography>
      )}

      {/* Category selection buttons */}
      <Stack
        id="products"
        justifyContent="center"
        sx={{
          mt: "35px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "10px",
          width: "fit-content",
          marginX: "auto",
          borderRadius: "15px",
          padding: "12px",
        }}
      >
        {Object.keys(categoryMapping).map((categoryLabel) => (
          <Button
            key={categoryLabel}
            variant={
              categoryMapping[categoryLabel] === selectedCategory
                ? "contained"
                : "outlined"
            }
            color="primary"
            sx={{ color: "white" }}
            onClick={() => handleChangeCategory(categoryLabel)}
          >
            {categoryLabel}
          </Button>
        ))}
      </Stack>

      {/* Display products in a grid format */}
      <Grid container spacing={2} sx={{ marginY: "30px", mt: 0 }}>
        {selectedProducts.map((product, index) => (
          <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
            <HomeCard product={product} />
          </Grid>
        ))}
      </Grid>

      {/* Pagination component */}
      <Stack spacing={2} sx={{ display: "flex", alignItems: "center", my: 3 }}>
        <Pagination
          count={Math.ceil(storeProducts.length / itemsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Stack>
    </Container>
  );
};

export default Home;
